import type { ReactNode } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { Menu, Scroller, Tabs, TabsList, TabsTab } from '@highlight-cp/core-ui';

import { BetaBadge } from '../beta-badge';

export interface TabData {
  label: string;
  value?: string;
  /** visible, but not interactive */
  disabled?: boolean;
  /** not visible */
  hidden?: boolean;
  leftIcon?: ReactNode;
  isBeta?: boolean;
}

interface TabsNavProps {
  value: string;
  data: (TabData & {
    menu?: TabData[];
  })[];
  onChange: (value: string) => void;
  /** The theme's tab-row skin; `folder` sits on a shaded header above content on the page fill. */
  variant?: 'default' | 'folder';
}

const TAB_MENU_VALUE = 'tab-menu';

export const TabsNav = ({ value, data, onChange, variant }: TabsNavProps) => {
  return (
    <Tabs
      variant={variant}
      value={value}
      onChange={(value) => {
        if (!value || value === TAB_MENU_VALUE) return;

        onChange(value);
      }}
    >
      <TabsList>
        <Scroller
          // Keep Tab focus on the selected tab (APG tabs pattern). Chevron buttons remain
          // clickable/pointer-operable; Left/Right on tabs + trackpad still scroll.
          endControlProps={{ tabIndex: -1 }}
          startControlProps={{ tabIndex: -1 }}
        >
          {data.map((tab) => {
            if (tab.hidden) return;

            if (tab.menu?.length) {
              const menuItems = tab.menu.filter((t) => !t.hidden);
              const isMenuActive = menuItems.some((menuTab) => menuTab.value === value);

              if (!menuItems.length) return;

              // `click-hover`, not `hover`: Mantine only wires hover triggers to mouse events, so a
              // hover-only menu is unreachable by keyboard.
              return (
                <Menu disabled={tab.disabled} key={tab.label} trigger="click-hover" withArrow>
                  <Menu.Target>
                    {/* Carrying the selected item's own value when the group is active lets Mantine
                        mark the trigger active and `aria-selected` itself; the sentinel value keeps
                        it inert otherwise. Re-selecting it just re-emits the current value. */}
                    <TabsTab
                      disabled={tab.disabled}
                      key={tab.label}
                      leftSection={tab.leftIcon}
                      rightSection={
                        <FiChevronDown
                          size={20}
                          color={
                            isMenuActive
                              ? 'var(--mantine-primary-color-filled)'
                              : 'var(--mantine-color-placeholder)'
                          }
                        />
                      }
                      value={isMenuActive ? value : TAB_MENU_VALUE}
                    >
                      {tab.label}
                    </TabsTab>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {menuItems.map((menuTab) => {
                      const isItemActive = value === menuTab.value;

                      return (
                        <Menu.Item
                          leftSection={menuTab.leftIcon}
                          key={menuTab.label}
                          disabled={menuTab.disabled}
                          value={menuTab.value ?? ''}
                          onClick={() => onChange(menuTab.value ?? '')}
                          // Menu.Item has no selected state, so the tint lives in the theme's Menu
                          // entry, keyed on this data attribute (see menu.module.css).
                          // `aria-current` carries the same state for assistive tech, since a tint
                          // alone conveys it by colour only.
                          aria-current={isItemActive ? 'true' : undefined}
                          mod={{ active: isItemActive }}
                        >
                          {menuTab.label}
                        </Menu.Item>
                      );
                    })}
                  </Menu.Dropdown>
                </Menu>
              );
            }

            return (
              <TabsTab
                key={tab.label}
                leftSection={tab.leftIcon}
                value={tab.value ?? ''}
                disabled={tab.disabled}
                rightSection={tab.isBeta && <BetaBadge />}
              >
                {tab.label}
              </TabsTab>
            );
          })}
        </Scroller>
      </TabsList>
    </Tabs>
  );
};
